import { useMemo, useState } from "react";
import { ListOrdered, Activity } from "lucide-react";
import { FEED_SCRIPT, LIVE_SCENARIOS, LIVE_SYSTEMS } from "@/lib/liveOps";

const FILTERS = ["All Clients", "FTD", "KYC", "Activation", "Retention", "Withdrawal", "Automated", "Human Escalation", "Suppressed", "High Value", "SLA Risk"];

const matches = (s, f) => {
  switch (f) {
    case "All Clients": return true;
    case "Activation": return ["nudge", "multilingual"].includes(s.id);
    case "Automated": return s.action.kind === "automated";
    case "Human Escalation": return s.action.kind === "human";
    case "Suppressed": return s.id === "suppressed";
    case "High Value": return s.client.segment === "High Value";
    case "SLA Risk": return s.action.kind === "human" && (s.slaBreach === true || s.action.task.slaMinutes <= 15);
    default: return s.category === f;
  }
};

const statusOf = (s) => {
  if (s.id === "suppressed") return "Governed";
  if (s.id === "organic") return "Monitoring";
  if (s.id === "healthy") return "No action";
  if (s.action.kind === "human") return "Open";
  return "Awaiting outcome";
};

export default function OperationsView() {
  const [filter, setFilter] = useState("All Clients");
  const rows = useMemo(() => LIVE_SCENARIOS.filter((s) => matches(s, filter)), [filter]);

  return (
    <div className="space-y-5" data-testid="operations-view">
      <div>
        <div className="text-[10px] font-mono tracking-widest text-emerald-500 uppercase mb-1">Operations View · Simulated demo data</div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
          <ListOrdered className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Operational Control Centre
        </h1>
        <p className="text-sm text-zinc-400 mt-1">What is happening right now, which clients are stuck, and which teams need to act.</p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md px-4 py-3 flex flex-wrap gap-x-6 gap-y-2" data-testid="ops-system-health">
        {LIVE_SYSTEMS.map((s) => (
          <div key={s.key} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-semibold text-zinc-200">{s.name}</span>
            <span className="text-[9px] font-mono text-emerald-500">CONNECTED</span>
            <span className="text-[9px] font-mono text-zinc-600">(simulated)</span>
          </div>
        ))}
        <span className="ml-auto text-[10px] font-mono text-zinc-500">Last event 3 sec ago · Queue latency 84 ms · Failed connectors 0</span>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="ops-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`ops-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors duration-200 ${
              filter === f ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/50" : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-600 hover:text-zinc-200"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]" data-testid="ops-queue">
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              {["Client", "Lifecycle State", "Issue", "Priority", "O.N.E.Tech Decision", "Current Action", "Assigned Team", "SLA", "Status"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-zinc-800/60 hover:bg-zinc-800/40 transition-colors duration-150" data-testid={`ops-row-${s.id}`}>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-semibold text-zinc-100">{s.client.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{s.client.ref}</div>
                </td>
                <td className="px-4 py-2.5 text-xs text-zinc-300">{s.boardFrom}</td>
                <td className="px-4 py-2.5 text-xs text-zinc-400 max-w-[200px] truncate" title={s.event.type}>{s.event.type}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-semibold ${s.decision.priority === "Critical" || s.decision.priority === "High" ? "text-red-400" : s.decision.priority === "Medium" ? "text-amber-400" : "text-zinc-500"}`}>
                    {s.decision.priority}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-zinc-300 max-w-[220px] truncate" title={s.decision.type}>{s.decision.type}</td>
                <td className="px-4 py-2.5 text-xs text-zinc-400">
                  {s.action.kind === "automated" ? `${s.action.channel} — ${s.action.template}` : s.action.kind === "human" ? "CRM Task" : "Suppressed / None"}
                </td>
                <td className="px-4 py-2.5 text-xs text-zinc-300">{s.action.kind === "human" ? s.action.task.assignee : s.action.task?.assignee || "—"}</td>
                <td className="px-4 py-2.5 text-xs font-mono text-amber-400">{s.action.kind === "human" ? `${s.action.task.slaMinutes}:00` : s.action.task?.sla || "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${
                    statusOf(s) === "Open" ? "text-sky-400 border-sky-500/40 bg-sky-500/10"
                    : statusOf(s) === "Governed" ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
                    : "text-zinc-400 border-zinc-600 bg-zinc-500/10"
                  }`}>{statusOf(s)}</span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-6 py-8 text-center text-sm text-zinc-500">No cases in this filter.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="ops-recent-events">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
          <span className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Recent operational events (simulated)</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5">
          {FEED_SCRIPT.slice(0, 10).map((e) => (
            <div key={`${e.t}-${e.client}`} className="text-[11px]">
              <span className="font-mono text-zinc-500">{e.t}</span>
              <span className="text-zinc-600"> | </span>
              <span className="text-emerald-400 font-semibold">{e.type}</span>
              <span className="text-zinc-600"> | </span>
              <span className="text-zinc-400">{e.client} — {e.detail}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
